import {abilityFeature} from '../abilityFeature';

/**
 * Designer ability for panel visibility checks
 * This can be used to create abilities that depend on specific panels being visible
 *
 * Config format: Array of panel names
 * The ability is valid when ANY of the specified panels are visible (OR logic)
 *
 * Example: [ADD_PANEL, SITE_SETTINGS_PANEL] - ability is valid when either panel is visible
 */
export const visiblePanels = abilityFeature({
  key: 'visiblePanels',
  isValid: function isVisiblePanelsValid({PanelsStore}, panelNames) {
    // If no panels specified, ability is invalid
    if (!panelNames || panelNames.length === 0) {
      return false;
    }

    const dockedPanel = PanelsStore?.paneState?.docked?.panelName;
    const leftPanel = PanelsStore?.paneState?.left?.panelName;

    /**
     * If there is a panel active in the left, the docked panel is not visible.
     */
    const visiblePanel = leftPanel !== null ? leftPanel : dockedPanel;

    if (!visiblePanel) {
      return false;
    }

    const activePath = PanelsStore?.panels.paths[visiblePanel];

    // Check if any of the specified panels are active (OR logic)
    return panelNames.some((panelName) => activePath?.includes(panelName));
  },
});

/**
 * Designer ability for panel visibility checks
 * This can be used to create abilities that depend on specific panels being hidden
 *
 * Config format: Array of panel names
 * The ability is valid when NONE of the specified panels are visible (AND logic for inactive state)
 *
 * Example: [ADD_PANEL, SITE_SETTINGS_PANEL] - ability is valid when neither panel is visible
 */
export const hiddenPanels = abilityFeature({
  key: 'hiddenPanels',
  isValid: function isHiddenPanelsValid({PanelsStore}, panelNames) {
    // If no panels specified, ability is always valid
    if (!panelNames || panelNames.length === 0) {
      return true;
    }

    const dockedPanel = PanelsStore?.paneState?.docked?.panelName;
    const leftPanel = PanelsStore?.paneState?.left?.panelName;

    const visiblePanel = leftPanel !== null ? leftPanel : dockedPanel;

    if (!visiblePanel) {
      return true;
    }

    // Check if none of the specified panels are open (AND logic for hidden state)
    return panelNames.every((panelName) => panelName !== visiblePanel);
  },
});
